import React, { useContext, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Private } from "layouts/Private";
import { Public } from "layouts/Public";
import Dashboard from "../pages/Dashboard";
import { Login } from "pages/Login";
import { EsqueciSenha } from "pages/Login/EsqueciSenha";
import { NovaSenha } from "pages/Login/NovaSenha";
import { MinhaConta } from "pages/MinhaConta";
import { PerfisDeEmpresa } from "pages/PerfisDeEmpresa";
import { Projetistas } from "pages/Projetistas";
import { Linhas } from "pages/Linhas";
import { LiberacoesClientesCEM } from "pages/LiberacoesClientesCEM";
import { Liberacoes } from "pages/Liberacoes";
import { LiberacoesPadrao } from "pages/LiberacoesPadrao";
import { LiberacoesDiversas } from "pages/LiberacoesDiversas";
import { MateriaisLiberadosDiversa } from "pages/LiberacoesDiversas/MateriaisLiberados";
import { AdicionarMateriaisDiversa } from "pages/LiberacoesDiversas/MateriaisLiberados/AdicionarMateriais";
import { LiberacoesMOBI } from "pages/LiberacoesMOBI";
import { LiberacoesMOBIDetalhe } from "pages/LiberacoesMOBIDetalhe";
import { Notificacoes } from "pages/Notificacoes";
import { Tipologias } from "pages/Tipologias";
import { TipologiaDetalhe, ConsultaTipologia } from "pages/Tipologias/detalhe";
import { Solicitacoes } from "pages/Solicitacoes";
import SolicitacaoDetalhePage from "pages/Solicitacoes/Detalhe";
import { Empresas } from "pages/Empresas";
import { CadastroDeEmpresas } from "pages/Empresas/Formulario/telaCadastros";
import { CadastroEmpresaProvider } from "contexts/cadastroEmpresaContext";
import { QuadroTarefas } from "pages/QuadroTarefas";
import { TiposAtividade } from "pages/TiposAtividade";
import { Aplicabilidades } from "pages/Aplicabilidades";
import { ConsultaAplicabilidades } from "pages/Aplicabilidades/Consulta";
import { DetalheComponente } from "pages/Aplicabilidades/DetalheComponente";
import { ProdutosN1 } from "pages/ProdutosN1";
import { CadastroPerfil } from "pages/ProdutosN1/Perfil";
import { CadastroComponente } from "pages/ProdutosN1/Componente";
import { CadastroVidro } from "pages/ProdutosN1/Vidro";
import { CadastroSuperficie } from "pages/ProdutosN1/Superficie";
import { CadastroKit } from "pages/ProdutosN1/Kit";
import { CadastroAcabamento } from "pages/ProdutosN1/Acabamento";
import { TabelasPrecos } from "pages/TabelasPrecos";
import { TabelaPrecoEdicao } from "pages/TabelasPrecos/Edicao";
import { ExigeRecurso } from "contexts/acessoRecursosContext";
import {
  Unidades,
  TiposVidro,
  TiposAcabamento,
  TiposTecido,
  ClassesIds,
  Cores,
  Grupos,
  Prenativos,
  Ligas,
} from "pages/CadastrosGerais";
import { Referencias } from "pages/CadastrosGerais/Referencias";
import { Catalogos } from "pages/CadastrosGerais/Catalogos";
import { getTokenFromUrl } from "shared/helpers/tokenHelper";
import { TokenContext } from "contexts/tokenContext";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "shared/hooks/auth";
import configs, { dashboardDisponivel } from "Config";
import { paginaEntradaMof } from "shared/helpers/paginaEntrada";

// Destino real de "/", das rotas desconhecidas e — quando o Dashboard não existe neste ambiente —
// de /dashboardMof, que é o que o MOF antigo envia no login (ver shared/helpers/paginaEntrada.ts).
const EntradaMof = () => {
  const { signed, havePermission } = useAuth();
  if (!signed) return <Navigate to="/login" replace />;
  return <Navigate to={paginaEntradaMof(havePermission, dashboardDisponivel())} replace />;
};

// Reporte do PO de 31/08: enquanto a sessao do hashId nao hidrata, a rota de entrada mostra
// este aviso curto em vez de redirecionar para /login (que devolveria o usuario ao MOF antigo).
const TelaCarregandoSessao = () => (
  <div style={{ padding: 32, textAlign: "center", color: "#555" }}>Carregando sua sessao...</div>
);

const AppRoutes = () => {
  const { setToken } = useContext(TokenContext);
  const { renovarSessao, signOut, signed } = useAuth();

  const handleToken = async () => {
    console.log("[handleToken] Iniciando carregamento de token...");
    
    // Primeiro, tenta obter token da URL (se houver hashId)
    await getTokenFromUrl();
    
    // Depois, verifica se já existe token no sessionStorage
    const token = sessionStorage.getItem("@MOF:token");
    const storagedUser = sessionStorage.getItem("@MOF:user");
    
    console.log("[handleToken] Token no sessionStorage:", token ? "existe" : "não existe");
    console.log("[handleToken] User no sessionStorage:", storagedUser ? "existe" : "não existe");
    console.log("[handleToken] Signed:", signed);
    
    // Validação: verifica se token existe e tem formato válido de JWT
    if (!token || token.trim() === "" || token.split(".").length !== 3) {
      console.warn("⚠️ Token inválido ou ausente - pulando decodificação");
      console.log("[handleToken] Token value:", token);
      console.log("[handleToken] Token length:", token?.length);
      console.log("[handleToken] Token parts:", token?.split(".").length);
      return;
    }
    
    try {
      const decodedJwt = jwtDecode(token);
      console.log("[handleToken] Token decodificado:", decodedJwt);
      
      // Verifica se o token ainda é válido (não expirou)
      const exp = decodedJwt.exp;
      if (exp && exp * 1000 <= Date.now()) {
        console.warn("⚠️ Token expirado - limpando sessionStorage");
        sessionStorage.clear();
        return;
      }
      
      // Se já tem usuário no sessionStorage e está autenticado, apenas atualiza o token
      if (storagedUser && signed) {
        console.log("[handleToken] Usuário já autenticado, apenas atualizando token");
        setToken(token);
        return;
      }
      
      // Se não tem usuário carregado ou não está autenticado, carrega o usuário
      const idUsuario = "IdUsuario" in decodedJwt
        ? parseInt(decodedJwt.IdUsuario as string)
        : 0;
      
      console.log("[handleToken] IdUsuario do token:", idUsuario);
      
      if (idUsuario > 0) {
        // Plano de 07/10/2026 (B1.3): a sessão é hidratada pelo backend novo (MinhaSessao), não mais
        // pelo login/changeUser do legado. Falha (401/403 — token inválido, usuário inativo) → /login.
        try {
          await renovarSessao();
          setToken(sessionStorage.getItem("@MOF:token") ?? token);
          console.log("[handleToken] Usuário carregado com sucesso");
        } catch (erroSessao) {
          console.warn("⚠️ Sessão recusada pelo servidor - indo para o login", erroSessao);
          signOut();
        }
      } else {
        console.warn("⚠️ IdUsuario não encontrado no token");
      }
    } catch (error) {
      console.error("❌ Erro ao decodificar token:", error);
      // Token corrompido - limpa sessionStorage
      sessionStorage.clear();
    }
  };

  // Reporte do PO de 31/08 (F05B-P3, item A.4): handleToken é ASSÍNCRONO. Quando o usuário chega
  // com ?hashId= (login, troca de usuário ou link do menu antigo), o primeiro render ainda não
  // está autenticado — e /dashboardMof, única rota com trava, mandava para /login, que é só um
  // link de volta ao MOF antigo. Enquanto a sessão hidrata, a rota espera em vez de redirecionar.
  const [carregandoSessao, setCarregandoSessao] = useState(true);

  useEffect(() => {
    handleToken().finally(() => setCarregandoSessao(false));
  }, []);

  const isAuthenticated = signed;

  // Frente 3 do plano de 02/09: NENHUMA rota monta antes de a sessão hidratar. O gate acima (que
  // valia só para /dashboardMof) deixava as demais páginas montarem durante o ObterTokenViaHashId;
  // a página disparava sua primeira chamada sem token no sessionStorage, o interceptor recebia 401
  // e devolvia o usuário ao MOF antigo (services/axios/api.ts) — era o "pisca" do primeiro login
  // em aba nova. Segurar o Router inteiro cobre todas as portas de entrada de uma vez.
  if (carregandoSessao) {
    return <TelaCarregandoSessao />;
  }

  return (
    <Router basename={configs.REACT_APP_BASENAME}>
      <Routes>
        <Route path="/" element={<EntradaMof />} />
        <Route
          path="/login"
          element={
            <Public>
              <Login />
            </Public>
          }
        />
        {/* Plano de 07/10/2026 (B2/B3): senhas e Minha Conta sem passar pelo MOF antigo. O e-mail de
            "Enviar dados de acesso" e o de "Esqueci minha senha" levam a /novaSenha/{IdPublico}. */}
        <Route
          path="/esqueciSenha"
          element={
            <Public>
              <EsqueciSenha />
            </Public>
          }
        />
        <Route
          path="/novaSenha/:pId"
          element={
            <Public>
              <NovaSenha />
            </Public>
          }
        />
        <Route
          path="/meuPrimeiroAcesso/:pId"
          element={
            <Public>
              <NovaSenha primeiroAcesso />
            </Public>
          }
        />
        <Route
          path="/minhaConta"
          element={
            <Private>
              <MinhaConta />
            </Private>
          }
        />
        <Route
          path="/dashboardMof"
          element={
            // O gate de sessão agora segura o Router inteiro (Frente 3, acima) — quando esta rota
            // renderiza, a hidratação já terminou; sem sessão, é /login mesmo.
            // Sem Dashboard neste ambiente, a rota vira a página de entrada do usuário.
            !dashboardDisponivel() ? (
              <EntradaMof />
            ) : isAuthenticated ? (
              <Private>
                <Dashboard />
              </Private>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/perfisDeEmpresa"
          element={
            <Private>
              <PerfisDeEmpresa />
            </Private>
          }
        />
        <Route
          path="/projetistas"
          element={
            <Private>
              <Projetistas />
            </Private>
          }
        />
        <Route
          path="/linhas"
          element={
            <Private>
              <Linhas />
            </Private>
          }
        />
        <Route
          path="/tipologiasMof"
          element={
            <Private>
              <Tipologias />
            </Private>
          }
        />
        <Route
          path="/tipologiasMof/:id"
          element={
            <Private>
              <TipologiaDetalhe />
            </Private>
          }
        />
        <Route
          path="/consultaTipologiasMof/:id"
          element={
            <Private>
              <ConsultaTipologia />
            </Private>
          }
        />
        <Route
          path="/solicitacoesMof"
          element={
            <Private>
              <Solicitacoes />
            </Private>
          }
        />
        <Route
          path="/solicitacoesMof/:id"
          element={
            <Private>
              <SolicitacaoDetalhePage />
            </Private>
          }
        />
        <Route
          path="/liberacoesMof"
          element={
            <Private>
              <LiberacoesClientesCEM />
            </Private>
          }
        />
        <Route
          path="/liberacoesMof/:empresaId"
          element={
            <Private>
              <Liberacoes />
            </Private>
          }
        />
        <Route
          path="/liberacoesPadrao"
          element={
            <Private>
              <LiberacoesPadrao />
            </Private>
          }
        />
        <Route
          path="/liberacoesDiversas"
          element={
            <Private>
              <LiberacoesDiversas />
            </Private>
          }
        />
        <Route
          path="/liberacoesDiversas/:id/materiais"
          element={
            <Private>
              <MateriaisLiberadosDiversa />
            </Private>
          }
        />
        <Route
          path="/liberacoesDiversas/:id/materiais/adicionar"
          element={
            <Private>
              <AdicionarMateriaisDiversa />
            </Private>
          }
        />
        <Route
          path="/liberacoesMobi"
          element={
            <Private>
              <LiberacoesMOBI />
            </Private>
          }
        />
        <Route
          path="/liberacoesMobiMof/:inquilinoId"
          element={
            <Private>
              <LiberacoesMOBIDetalhe />
            </Private>
          }
        />
        <Route
          path="/notificacoesMof"
          element={
            <Private>
              <Notificacoes />
            </Private>
          }
        />
        <Route
          path="/quadroTarefas"
          element={
            <Private>
              <QuadroTarefas />
            </Private>
          }
        />
        <Route
          path="/tiposAtividade"
          element={
            <Private>
              <TiposAtividade />
            </Private>
          }
        />
        <Route
          path="/empresasMof"
          element={
            <Private>
              <Empresas />
            </Private>
          }
        />
        <Route
          path="/empresasMof/cadastrarEmpresa"
          element={
            <Private>
              <CadastroEmpresaProvider>
                <CadastroDeEmpresas />
              </CadastroEmpresaProvider>
            </Private>
          }
        />
        <Route
          path="/empresasMof/editar/:id"
          element={
            <Private>
              <CadastroEmpresaProvider>
                <CadastroDeEmpresas />
              </CadastroEmpresaProvider>
            </Private>
          }
        />
        <Route
          path="/aplicabilidades"
          element={<Navigate to="/aplicabilidades/cadastros" replace />}
        />
        <Route
          path="/aplicabilidades/cadastros/:empresaId?"
          element={
            <Private>
              <ExigeRecurso recurso="aplicabilidades">
                <Aplicabilidades />
              </ExigeRecurso>
            </Private>
          }
        />
        <Route
          path="/aplicabilidades/consulta/:empresaId?"
          element={
            <Private>
              <ExigeRecurso recurso="aplicabilidades">
                <ConsultaAplicabilidades />
              </ExigeRecurso>
            </Private>
          }
        />
        <Route
          path="/aplicabilidades/cadastros/:empresaId/componente/:componenteBaseId"
          element={
            <Private>
              <ExigeRecurso recurso="aplicabilidades">
                <DetalheComponente />
              </ExigeRecurso>
            </Private>
          }
        />
        <Route
          path="/produtosN1"
          element={
            <Private>
              <ProdutosN1 />
            </Private>
          }
        />
        {/* FASE 05 — Ajustes 25/08 (item 1 / decisão F05A-P1 de 26/08): Acabamentos ganhou
            página própria. É a MESMA tela de Produtos, fixa em acabamentos e sem o seletor
            de tipo — daí a rota vir antes das rotas de cadastro `/acabamentos/:id`. */}
        <Route
          path="/acabamentosN1"
          element={
            <Private>
              <ProdutosN1 apenasAcabamentos />
            </Private>
          }
        />
        <Route
          path="/produtosN1/perfis/novo"
          element={
            <Private>
              <CadastroPerfil />
            </Private>
          }
        />
        <Route
          path="/produtosN1/perfis/:id"
          element={
            <Private>
              <CadastroPerfil />
            </Private>
          }
        />
        <Route
          path="/produtosN1/componentes/novo"
          element={
            <Private>
              <CadastroComponente />
            </Private>
          }
        />
        <Route
          path="/produtosN1/componentes/:id"
          element={
            <Private>
              <CadastroComponente />
            </Private>
          }
        />
        <Route
          path="/produtosN1/vidros/novo"
          element={
            <Private>
              <CadastroVidro />
            </Private>
          }
        />
        <Route
          path="/produtosN1/vidros/:id"
          element={
            <Private>
              <CadastroVidro />
            </Private>
          }
        />
        <Route
          path="/produtosN1/superficies/novo"
          element={
            <Private>
              <CadastroSuperficie />
            </Private>
          }
        />
        <Route
          path="/produtosN1/superficies/:id"
          element={
            <Private>
              <CadastroSuperficie />
            </Private>
          }
        />
        <Route
          path="/produtosN1/kits/novo"
          element={
            <Private>
              <CadastroKit />
            </Private>
          }
        />
        <Route
          path="/produtosN1/kits/:id"
          element={
            <Private>
              <CadastroKit />
            </Private>
          }
        />
        <Route
          path="/produtosN1/acabamentos/novo"
          element={
            <Private>
              <CadastroAcabamento />
            </Private>
          }
        />
        <Route
          path="/produtosN1/acabamentos/:id"
          element={
            <Private>
              <CadastroAcabamento />
            </Private>
          }
        />
        {/* FASE 05 — Cadastros Gerais (plano de 23/09/2026): cadastros da Gerência que saíram do MOF antigo. */}
        <Route
          path="/cadastros/unidades"
          element={
            <Private>
              <Unidades />
            </Private>
          }
        />
        <Route
          path="/cadastros/tipos-vidro"
          element={
            <Private>
              <TiposVidro />
            </Private>
          }
        />
        <Route
          path="/cadastros/tipos-acabamento"
          element={
            <Private>
              <TiposAcabamento />
            </Private>
          }
        />
        <Route
          path="/cadastros/classes-ids"
          element={
            <Private>
              <ClassesIds />
            </Private>
          }
        />
        <Route
          path="/cadastros/cores"
          element={
            <Private>
              <Cores />
            </Private>
          }
        />
        <Route
          path="/cadastros/grupos"
          element={
            <Private>
              <Grupos />
            </Private>
          }
        />
        <Route
          path="/cadastros/prenativos"
          element={
            <Private>
              <Prenativos />
            </Private>
          }
        />
        <Route
          path="/cadastros/ligas"
          element={
            <Private>
              <Ligas />
            </Private>
          }
        />
        <Route
          path="/cadastros/referencias"
          element={
            <Private>
              <Referencias />
            </Private>
          }
        />
        <Route
          path="/cadastros/catalogos"
          element={
            <Private>
              <Catalogos />
            </Private>
          }
        />
        <Route
          path="/configuracoes/tecidos"
          element={
            <Private>
              <TiposTecido />
            </Private>
          }
        />
        {/* FASE 05 — S4-B2/PO 2026-05-13: Tabelas de Preços (Materiais) */}
        <Route
          path="/tabelas-precos"
          element={
            <Private>
              <ExigeRecurso recurso="tabelasPreco">
                <TabelasPrecos />
              </ExigeRecurso>
            </Private>
          }
        />
        <Route
          path="/tabelas-precos/:id/editar"
          element={
            <Private>
              <ExigeRecurso recurso="tabelasPreco">
                <TabelaPrecoEdicao />
              </ExigeRecurso>
            </Private>
          }
        />
        <Route path="*" element={<EntradaMof />} />
      </Routes>
    </Router>
  );
};

export default AppRoutes;
